import { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { profile } = useAuth(); // Must stay as first hook

  // Hooks cannot be conditional
  const [emailNotificationEnabled, setEmailNotificationEnabled] = useState(() => {
    const saved = localStorage.getItem("emailNotificationEnabled");
    return saved ? JSON.parse(saved) : true;
  });

  const [pushNotificationEnabled, setPushNotificationEnabled] = useState(() => {
    const saved = localStorage.getItem("pushNotificationEnabled");
    return saved ? JSON.parse(saved) : false;
  });

  const [userPhoneNumber, setUserPhoneNumber] = useState(() => {
    return localStorage.getItem("userPhoneNumber") || "";
  });

  const [lastNotificationSent, setLastNotificationSent] = useState(() => {
    const stored = localStorage.getItem("lastNotificationSent");
    return stored ? JSON.parse(stored) : { timestamp: 0 };
  });

  const validatePhoneNumber = (phone) => {
    const regex = /^\+[1-9]\d{8,14}$/;
    return regex.test(phone);
  };

  // Fetch org admins (with detailed logs)
  const fetchOrgAdmins = async (orgId, region, currentUserEmail) => {
    console.log("🔥 fetchOrgAdmins called with:", { orgId, region, currentUserEmail });

    if (!orgId) {
      console.warn("⚠️ Missing orgId in fetchOrgAdmins");
      return [];
    }

    try {
      const apiUrl = `https://iffud7s7y3.execute-api.ap-southeast-2.amazonaws.com/prod/api/org/${orgId}/users`;

      console.log("🌐 Fetching org users from:", apiUrl);

      const response = await axios.get(apiUrl, {
        headers: { Region: region }
      });

      const users = response.data || [];

      console.log("📥 All org users received:", users);

      const admins = users.filter(
        (u) => u.role === "ADMIN" || u.hasDashboard === true
      );

      console.log("👑 Filtered admins:", admins);

      const filteredAdmins = admins.filter((a) => a.email !== currentUserEmail);

      console.log("✨ Admins to notify (excluding current user):", filteredAdmins);

      return filteredAdmins;

    } catch (err) {
      console.error("❌ Error fetching admins:", err);
      return [];
    }
  };

  // Notification sender
  const sendCriticalRiskNotification = useCallback(
    async (criticalRisks, userData, region) => {

      console.log("🚨 sendCriticalRiskNotification triggered", {
        criticalRisks,
        userData,
        region,
        profile,
      });

      if (!profile?.orgId) {
        console.warn("⛔ profile.orgId missing, skipping notifications");
        return [];
      }

      const orgId = profile.orgId;

      const notifications = [];
      const methods = [];
      const currentUserEmail = userData?.email;

      console.log("👤 Current user email:", currentUserEmail);

      const orgAdmins = await fetchOrgAdmins(orgId, region, currentUserEmail);

      console.log("📌 Org admins returned:", orgAdmins);

      const recipients = [
        currentUserEmail,
        ...orgAdmins.map((a) => a.email),
      ].filter(Boolean);

      console.log("📧 Final recipients list for email and SMS:", recipients);

      // EMAIL notifications
      if (emailNotificationEnabled) {
        console.log("📧 Email notifications enabled");

        for (const email of recipients) {
          console.log(`✉️ Preparing email to: ${email}`);

          const payload = {
            to: email,
            subject: `CRITICAL RISK ALERT - ${criticalRisks.length} detected`,
            template: "critical-risk-alert",
            data: {
              userName: userData?.name,
              criticalRisks,
              totalCriticalRisks: criticalRisks.length,
              timestamp: new Date().toLocaleString(),
              dashboardUrl: window.location.origin,
            },
          };

          notifications.push(
            axios.post(
              "https://8sw4eku482.execute-api.ap-southeast-2.amazonaws.com/prod/api/notifications/critical-risk-alert",
              payload,
              { headers: { "Content-Type": "application/json", Region: region } }
            )
            .then((resp) => {
              console.log(`✅ Email successfully sent to ${email}`);
              methods.push("email");
            })
            .catch((err) => {
              console.error(`❌ Email failed for ${email}:`, err);
            })
          );
        }
      } else {
        console.log("📧 Email notifications disabled.");
      }

      // SMS notifications
      if (pushNotificationEnabled) {
        console.log("📱 SMS notifications enabled");

        if (!validatePhoneNumber(userPhoneNumber)) {
          console.warn("⚠️ Invalid phone number:", userPhoneNumber);
        } else {
          console.log(`📱 Sending SMS to user: ${userData?.["cognito:username"]}`);

          notifications.push(
            axios.post(
              "https://9925qndwwe.execute-api.ap-southeast-2.amazonaws.com/prod/api/notifications/send-sms",
              null,
              {
                params: {
                  message: `CRITICAL RISK ALERT. ${criticalRisks.length} critical risks detected.`,
                  userId: userData?.["cognito:username"],
                },
                headers: { Region: region }
              }
            )
            .then(() => {
              console.log("✅ SMS sent");
              methods.push("SMS");
            })
            .catch((err) => {
              console.error("❌ SMS failed:", err);
            })
          );
        }
      } else {
        console.log("📱 SMS notifications disabled.");
      }

      await Promise.allSettled(notifications);

      const stamp = { timestamp: Date.now() };
      setLastNotificationSent(stamp);
      localStorage.setItem("lastNotificationSent", JSON.stringify(stamp));

      console.log("🕒 Updated lastNotificationSent:", stamp);

      return methods;
    },
    [
      emailNotificationEnabled,
      pushNotificationEnabled,
      userPhoneNumber,
      profile,
    ]
  );

  // Wait until profile.orgId is available (AFTER hooks)
  if (!profile || !profile.orgId) {
    if (window.location.pathname.startsWith("/consent/")) {
      return (
        <NotificationContext.Provider value={{
          emailNotificationEnabled: false,
          pushNotificationEnabled: false,
          userPhoneNumber: "",
          setEmailNotificationEnabled: () => {},
          setPushNotificationEnabled: () => {},
          setUserPhoneNumber: () => {},
          validatePhoneNumber: () => false,
          sendCriticalRiskNotification: async () => [],
          lastNotificationSent: { timestamp: 0 },
        }}>
          {children}
        </NotificationContext.Provider>
      );
    }
    console.log("⏳ Waiting for profile.orgId...");
    return null;
  }

  return (
    <NotificationContext.Provider
      value={{
        emailNotificationEnabled,
        pushNotificationEnabled,
        userPhoneNumber,

        setEmailNotificationEnabled,
        setPushNotificationEnabled,
        setUserPhoneNumber,

        validatePhoneNumber,
        sendCriticalRiskNotification,
        lastNotificationSent,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
